/**
 * REST calls to the price service (`/v1/markets`, `/v1/prices`,
 * `/v1/candles`), authenticated with the short-lived price token.
 */

import {
  isServiceExchange,
  marketKindOf,
  type ServiceExchange,
  type ServiceMarket,
} from "./exchanges";
import type { ServiceInterval } from "./intervals";
import {
  toPriceUpdate,
  toServiceCandle,
  type PriceUpdate,
  type RawServicePrice,
  type ServiceCandle,
} from "./price-service.types";
import type { PriceTokenManager } from "./price-token";

/** `/v1/candles` returns at most this many candles. */
export const MAX_CANDLES_PER_REQUEST = 1000;
/** `/v1/prices` accepts at most this many markets per request. */
export const MAX_PRICE_MARKETS_PER_REQUEST = 200;

export interface CandleRequest {
  exchange: ServiceExchange;
  symbol: string;
  interval: ServiceInterval;
  limit: number;
  /** Unix seconds; omitted for the latest candles. */
  end?: number;
}

interface RawMarketsResponse {
  markets?: Array<{
    exchange?: unknown;
    symbol?: unknown;
    priced?: unknown;
    market?: unknown;
  }>;
}

/** Every listed token: symbols per venue and 24h futures volume per token. */
export interface ServiceTokenList {
  exchanges: Partial<Record<ServiceExchange, string[]>>;
  /** `<BASE>USDT` -> summed 24h futures quote volume (USDT). Empty on older services. */
  volumes: Record<string, number>;
}

interface RawTokenListResponse {
  exchanges?: Record<string, unknown>;
  volume_24h?: Record<string, unknown>;
}

export class PriceServiceRest {
  private readonly fetchFn: typeof fetch;

  constructor(
    private readonly tokens: PriceTokenManager,
    fetchFn?: typeof fetch
  ) {
    this.fetchFn = fetchFn ?? ((input, init) => fetch(input, init));
  }

  /**
   * Venues that list a token, each with whether the service prices it now.
   * `base` is e.g. `BTC`.
   */
  async getMarkets(base: string): Promise<ServiceMarket[]> {
    const body = await this.get<RawMarketsResponse>("/v1/markets", {
      symbol: base,
    });
    const markets: ServiceMarket[] = [];
    for (const row of body.markets ?? []) {
      if (typeof row.exchange !== "string" || !isServiceExchange(row.exchange)) {
        continue;
      }
      if (typeof row.symbol !== "string" || !row.symbol) continue;
      markets.push({
        exchange: row.exchange,
        symbol: row.symbol.toUpperCase(),
        market:
          row.market === "spot" || row.market === "futures"
            ? row.market
            : marketKindOf(row.exchange),
        priced: row.priced !== false,
      });
    }
    return markets;
  }

  /** Every symbol each venue lists (`/v1/markets` without a symbol). */
  async getTokenList(): Promise<ServiceTokenList> {
    const body = await this.get<RawTokenListResponse>("/v1/markets", {});
    const exchanges: ServiceTokenList["exchanges"] = {};
    for (const [exchange, symbols] of Object.entries(body.exchanges ?? {})) {
      if (!isServiceExchange(exchange) || !Array.isArray(symbols)) continue;
      exchanges[exchange] = symbols
        .filter((symbol): symbol is string => typeof symbol === "string" && !!symbol)
        .map((symbol) => symbol.toUpperCase());
    }
    const volumes: Record<string, number> = {};
    for (const [symbol, volume] of Object.entries(body.volume_24h ?? {})) {
      const value = typeof volume === "number" ? volume : Number(volume);
      if (Number.isFinite(value) && value >= 0) volumes[symbol.toUpperCase()] = value;
    }
    return { exchanges, volumes };
  }

  async getPrices(
    markets: Array<{ exchange: ServiceExchange; symbol: string }>
  ): Promise<PriceUpdate[]> {
    const out: PriceUpdate[] = [];
    for (let i = 0; i < markets.length; i += MAX_PRICE_MARKETS_PER_REQUEST) {
      const chunk = markets.slice(i, i + MAX_PRICE_MARKETS_PER_REQUEST);
      const body = await this.get<{ prices?: RawServicePrice[] }>(
        "/v1/prices",
        { m: chunk.map((m) => `${m.exchange}:${m.symbol}`).join(",") }
      );
      for (const row of body.prices ?? []) {
        // Rows older than 10 minutes count as no price.
        const update = toPriceUpdate(row, { checkTs: true });
        if (update) out.push(update);
      }
    }
    return out;
  }

  /** Oldest first; the last candle may still be open. */
  async getCandles(request: CandleRequest): Promise<ServiceCandle[]> {
    const params: Record<string, string> = {
      exchange: request.exchange,
      symbol: request.symbol,
      interval: request.interval,
      limit: String(
        Math.max(1, Math.min(MAX_CANDLES_PER_REQUEST, Math.ceil(request.limit)))
      ),
    };
    if (request.end !== undefined) {
      params.end = String(Math.floor(request.end));
    }
    const body = await this.get<{ candles?: unknown[] }>("/v1/candles", params);
    const candles: ServiceCandle[] = [];
    for (const raw of body.candles ?? []) {
      const candle = toServiceCandle(raw);
      if (candle) candles.push(candle);
    }
    return candles;
  }

  private async get<T>(
    path: string,
    params: Record<string, string>,
    retried = false
  ): Promise<T> {
    const token = await this.tokens.get();
    const query = new URLSearchParams(params).toString();
    const response = await this.fetchFn(`${token.url}${path}?${query}`, {
      headers: { Authorization: `Bearer ${token.token}` },
    });

    if (response.status === 401 && !retried) {
      this.tokens.invalidate(token.token);
      return this.get<T>(path, params, true);
    }
    if (response.status === 429) {
      throw new Error("Price service rate limit (429)");
    }
    if (!response.ok) {
      throw new Error(`Price service request failed with status ${response.status}`);
    }
    return (await response.json()) as T;
  }
}
